import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { GraduationCap, User, Lock } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/store/auth';
import { toast } from '@/components/ui/Toast';
import { useT } from '@/store/i18n';

export function Login() {
  const t = useT();
  const { user, login, loading, error } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  if (user) return <Navigate to="/" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      await login(username, password);
      toast.success(t('login.success'));
    } catch {
      // error already in store
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Brand panel */}
      <div className="relative hidden lg:flex flex-col items-center justify-center p-12 bg-gradient-to-br from-brand-500 via-brand-600 to-brand-800 text-white overflow-hidden">
        <div
          aria-hidden
          className="absolute -top-1/2 -left-1/2 w-[200%] h-[200%]"
          style={{
            background:
              'radial-gradient(circle at 30% 50%, rgba(255,255,255,0.12) 0%, transparent 60%)',
          }}
        />
        <div className="relative z-10 max-w-sm text-center">
          <div className="w-20 h-20 rounded-3xl bg-white/15 backdrop-blur-sm mx-auto flex items-center justify-center mb-7">
            <GraduationCap className="w-10 h-10" />
          </div>
          <h1 className="text-3xl font-bold leading-tight mb-2">
            {t('system.title')}
          </h1>
          <p className="text-lg text-white/90 mb-8">{t('system.school')}</p>
          <p className="text-sm text-white/70 leading-relaxed">
            {t('login.tagline')}
          </p>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white flex items-center justify-center">
              <GraduationCap className="w-6 h-6" />
            </div>
            <div>
              <div className="text-base font-bold text-slate-900">
                {t('login.brand_short')}
              </div>
              <div className="text-xs text-slate-500">{t('system.school')}</div>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-slate-900">{t('login.title')}</h2>
          <p className="text-sm text-slate-500 mt-1.5 mb-8">
            {t('login.welcome')}
          </p>

          <form onSubmit={onSubmit} className="space-y-4">
            <Input
              label={t('login.username')}
              placeholder={t('login.username_placeholder')}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              leftIcon={<User className="w-4 h-4" />}
              autoComplete="username"
              required
              autoFocus
            />
            <Input
              label={t('login.password')}
              type="password"
              placeholder={t('login.password_placeholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              leftIcon={<Lock className="w-4 h-4" />}
              autoComplete="current-password"
              required
            />

            {error && (
              <div
                role="alert"
                className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              size="lg"
              loading={loading}
              className="w-full mt-2"
            >
              {t('login.submit')}
            </Button>
          </form>

          <div className="mt-10 text-center text-xs text-slate-400">
            {t('login.footer')}
          </div>
        </div>
      </div>
    </div>
  );
}
